import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Mutex } from 'async-mutex';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FileStorageInjectionToken, FileStorageServiceInterface } from '../../interfaces/file-storage.interface';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { JsonRecipientModel, RecipientModel, RecipientModelFromJson } from '../../models/low-level/recipient.model';
import { ConfigService } from '../../services/config/config.service';
import { isFileError } from '../../utilities/is-file-error';
import { LocalStorageCommonService } from './local-storage-common.service';

const KEY_RECIPIENTS = 'RECIPIENTS';

@Injectable({
    providedIn: 'root',
})
export class LocalStorageRecipientsService {
    private readonly recipientMutex: Mutex;
    private recipientsStorage: SavedRecipientsInterface[] = [];
    public constructor(
        private readonly configService: ConfigService,
        @Inject(FileStorageInjectionToken) private readonly fileStorage: FileStorageServiceInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly localStorageCommonService: LocalStorageCommonService,
    ) {
        this.recipientMutex = new Mutex();
    }

    public init(): Observable<void> {
        return this.fileStorage.loadCompressedData<JsonRecipientsInterface[]>(this.recipientMutex, KEY_RECIPIENTS).pipe(
            map((savedRecipients: JsonRecipientsInterface[]) => {
                savedRecipients.map((jsonRecipient) => {
                    const recipientModel = jsonRecipient.recipientsModel.map((recipient) => {
                        return new RecipientModelFromJson(recipient);
                    });

                    const recipients: SavedRecipientsInterface = {
                        id: jsonRecipient.id,
                        recipientsModel: recipientModel,
                    };
                    this.recipientsStorage.push(recipients);
                });
            }),
            catchError((error) => {
                if (isFileError(error) && error.code === 1) {
                    this.log.debug(
                        `MaterialUtilitiesService: getRecipientsFromLocalStorage: File was not found, returning default value instead`,
                    );
                }
                return of(undefined);
            }),
        );
    }

    public getRecipientsByNotificationId(id: NotificationIdentifierModel): RecipientModel[] | undefined {
        const storageId = this.generateId(id);

        return this.recipientsStorage.find((recipients) => recipients.id === storageId)?.recipientsModel;
    }

    public saveRecipientsByNotificationId(
        recipients: RecipientModel[],
        id: NotificationIdentifierModel,
    ): Observable<SavedRecipientsInterface[]> {
        const storageId = this.generateId(id);
        const recipientsStorage: SavedRecipientsInterface = {
            id: storageId,
            recipientsModel: recipients,
        };

        const i = this.recipientsStorage.findIndex((recipient) => recipient.id === storageId);
        if (i !== -1) {
            this.recipientsStorage[i] = recipientsStorage;
        } else {
            this.recipientsStorage.push(recipientsStorage);
        }

        return this.saveRecipientsToLocalStorage();
    }

    public saveRecipientsToLocalStorage(): Observable<SavedRecipientsInterface[]> {
        return this.fileStorage.storeCompressedData(this.recipientMutex, KEY_RECIPIENTS, this.recipientsStorage);
    }

    public removeRecipientsFromLocalStorage(id: NotificationIdentifierModel): Observable<SavedRecipientsInterface[]> {
        const storageId = this.generateId(id);
        const index = this.recipientsStorage.findIndex((recipient) => recipient.id === storageId);

        if (index !== -1) {
            this.recipientsStorage.splice(index, 1);
        }
        return this.saveRecipientsToLocalStorage();
    }

    public generateId(id: NotificationIdentifierModel): string {
        return KEY_RECIPIENTS + '_' + this.localStorageCommonService.makeId(id);
    }
}

export interface SavedRecipientsInterface {
    id: string;
    recipientsModel: RecipientModel[];
}

export interface JsonRecipientsInterface {
    id: string;
    recipientsModel: JsonRecipientModel[];
}
